import { supabase, supabaseAnonKey, supabaseUrl } from '@/lib/supabase';
import type {
  AdminActionInput,
  DashboardData,
  JsonRecord,
  ListResult,
  ProspectDiscoveryCandidate,
  ProspectDiscoveryData,
  ProspectDiscoverySearchResult,
  ProspectDiscoverySearchRow,
  ProspectOutreachLanguage,
  ProspectOutreachTemplate,
  ProspectPreview,
  SearchResults,
  StaffSession,
  CatalogDictionaryData,
  CatalogTerm,
  CatalogTermKind,
} from '@/types';

type AdminApiAction =
  | 'staff-session'
  | 'dashboard'
  | 'search'
  | 'user-detail'
  | 'business-detail'
  | 'list'
  | 'staff-list'
  | 'prospect-previews-list'
  | 'prospect-discovery-search'
  | 'prospect-discovery-google-live'
  | 'prospect-discovery-select'
  | 'prospect-discovery-list'
  | 'prospect-discovery-city-sweep'
  | 'prospect-discovery-retry'
  | 'prospect-discovery-stage'
  | 'prospect-discovery-update'
  | 'prospect-discovery-delete'
  | 'prospect-discovery-create-manual'
  | 'prospect-discovery-assign'
  | 'prospect-outreach-save'
  | 'prospect-whatsapp-send'
  | 'prospect-outreach-template-save'
  | 'prospect-outreach-template-delete'
  | 'prospect-discovery-create-draft'
  | 'prospect-discovery-import-media'
  | 'prospect-discovery-upload-image'
  | 'prospect-preview-save'
  | 'prospect-preview-archive'
  | 'catalog-dictionary-list'
  | 'catalog-term-upsert'
  | 'catalog-term-delete'
  | 'apply-action';

const CALLBACK_ACCESS_TOKEN_KEY = 'everyvisit.admin.callback-access-token';
let memoryAccessToken: string | undefined;

export function rememberAdminAccessToken(accessToken: string | null) {
  memoryAccessToken = accessToken ?? undefined;
  try {
    if (accessToken) {
      window.sessionStorage.setItem(CALLBACK_ACCESS_TOKEN_KEY, accessToken);
    } else {
      window.sessionStorage.removeItem(CALLBACK_ACCESS_TOKEN_KEY);
    }
  } catch {
    // Restrictive browser modes can disable session storage.
  }
}

function rememberedAdminAccessToken() {
  if (memoryAccessToken) return memoryAccessToken;
  try {
    return window.sessionStorage.getItem(CALLBACK_ACCESS_TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export async function adminRequest<T>(
  action: AdminApiAction,
  input: JsonRecord = {},
  accessToken?: string,
): Promise<T> {
  let token = accessToken ?? rememberedAdminAccessToken();
  if (!token) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    token = session?.access_token;
  }
  if (!token) throw new Error('Your session has expired. Sign in again.');

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 60_000);
  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/admin-api`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: supabaseAnonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, input }),
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Save timed out. Large catalogs can take a moment — try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(String(data?.error ?? `Admin API request failed (${response.status})`));
  }
  if (data?.error) throw new Error(String(data.error));
  return data?.data as T;
}

export const adminApi = {
  session: (accessToken?: string) =>
    adminRequest<StaffSession>('staff-session', {}, accessToken),
  dashboard: () => adminRequest<DashboardData>('dashboard'),
  search: (query: string) => adminRequest<SearchResults>('search', { query }),
  userDetail: (userId: string) =>
    adminRequest<JsonRecord>('user-detail', { userId }),
  businessDetail: (businessId: string) =>
    adminRequest<JsonRecord>('business-detail', { businessId }),
  list: (input: JsonRecord) => adminRequest<ListResult>('list', input),
  staffList: () => adminRequest<{ rows: JsonRecord[] }>('staff-list'),
  prospectPreviews: () =>
    adminRequest<{ rows: ProspectPreview[] }>('prospect-previews-list'),
  prospectDiscovery: () =>
    adminRequest<ProspectDiscoveryData>('prospect-discovery-list'),
  searchProspects: (input: {
    query: string;
    location: string;
    regionCode: string;
    languageCode: string;
    maxResults: number;
  }) => adminRequest<ProspectDiscoverySearchResult>('prospect-discovery-search', input),
  liveGoogleProspectDetails: (id: string) =>
    adminRequest<{
      place: ProspectDiscoverySearchRow | null;
      attribution?: { label: string; url: string; storageNotice: string };
    }>('prospect-discovery-google-live', { id }),
  selectProspects: (
    campaignId: string,
    selections: Array<{
      placeId: string;
      instagramUsername?: string;
      name?: string;
      category?: string;
      address?: string;
    }>,
  ) =>
    adminRequest<{ rows: Array<{ id: string; status: string }> }>(
      'prospect-discovery-select',
      { campaignId, selections },
    ),
  retryProspectDiscovery: (id: string) =>
    adminRequest<{ id: string }>('prospect-discovery-retry', { id }),
  queueProspectCitySweep: () =>
    adminRequest<{
      queued: boolean;
      request: {
        id: string;
        status: 'pending' | 'processing' | 'completed' | 'failed';
        location_query: string;
        campaign_id: string | null;
        stats: Record<string, unknown>;
        last_error: string | null;
        created_at: string;
        started_at: string | null;
        completed_at: string | null;
      };
    }>('prospect-discovery-city-sweep'),
  updateProspectStage: (
    id: string,
    stage: ProspectDiscoveryCandidate['outreach_stage'],
  ) =>
    adminRequest<{ id: string; outreach_stage: string }>(
      'prospect-discovery-stage',
      { id, stage },
    ),
  updateProspectInformation: (
    id: string,
    changes: Record<string, string>,
  ) =>
    adminRequest<{ id: string }>(
      'prospect-discovery-update',
      { id, changes },
    ),
  deleteProspect: (id: string) =>
    adminRequest<{ id: string }>('prospect-discovery-delete', { id }),
  createManualProspect: (input: {
    businessName: string;
    category: string;
    languageCode: ProspectOutreachLanguage;
    assignedTo: string | null;
    instagramUsername?: string;
  }) => adminRequest<{ id: string }>('prospect-discovery-create-manual', input),
  assignProspect: (id: string, assignedTo: string | null) =>
    adminRequest<{ id: string; assigned_to: string | null; assigned_at: string | null }>(
      'prospect-discovery-assign',
      { id, assignedTo },
    ),
  saveProspectOutreach: (input: {
    id: string;
    templateId: string | null;
    languageCode: ProspectOutreachLanguage;
    message: string;
    markContacted: boolean;
    markFollowUp?: boolean;
  }) => adminRequest<{
    id: string;
    outreach_stage: ProspectDiscoveryCandidate['outreach_stage'];
    outreach_template_id: string | null;
    outreach_language_code: ProspectOutreachLanguage;
    outreach_sender_name: string;
    outreach_message: string;
    outreach_message_updated_at: string;
    contacted_at: string | null;
    contacted_instagram_at?: string | null;
    followed_up_at: string | null;
  }>('prospect-outreach-save', input),
  sendProspectWhatsAppTemplate: (id: string, templateId?: string | null) =>
    adminRequest<{
      id: string;
      outreach_stage: ProspectDiscoveryCandidate['outreach_stage'];
      contacted_at: string | null;
      followed_up_at?: string | null;
      whatsapp_cloud_message_id: string | null;
      whatsapp_cloud_template: string | null;
      whatsapp_cloud_sent_at: string | null;
      to: string;
      businessName: string;
      templateLanguage: string;
    }>('prospect-whatsapp-send', { id, templateId: templateId || null }),
  saveProspectOutreachTemplate: (input: {
    id?: string;
    name: string;
    languageCode: ProspectOutreachLanguage;
    body: string;
    isDefault: boolean;
  }) => adminRequest<ProspectOutreachTemplate>('prospect-outreach-template-save', input),
  deleteProspectOutreachTemplate: (id: string) =>
    adminRequest<{ id: string }>('prospect-outreach-template-delete', { id }),
  importProspectWebsiteMedia: (candidateId: string, mediaId: string) =>
    adminRequest<{ mediaId: string; url: string }>(
      'prospect-discovery-import-media',
      { candidateId, mediaId },
    ),
  uploadProspectPreviewImage: (candidateId: string, image: string) =>
    adminRequest<{ path: string; url: string }>(
      'prospect-discovery-upload-image',
      { candidateId, image },
    ),
  createProspectDraft: (input: {
    candidateId: string;
    prospectName: string;
    content: ProspectPreview['content'];
    expiresAt: string | null;
    reviewed: true;
    publish?: boolean;
  }) => adminRequest<ProspectPreview>('prospect-discovery-create-draft', input),
  saveProspectPreview: (input: {
    id?: string;
    prospectName: string;
    status: 'draft' | 'published';
    content: ProspectPreview['content'];
    expiresAt: string | null;
  }) => adminRequest<ProspectPreview>('prospect-preview-save', input),
  archiveProspectPreview: (id: string) =>
    adminRequest<{ id: string }>('prospect-preview-archive', { id }),
  catalogDictionary: () =>
    adminRequest<CatalogDictionaryData>('catalog-dictionary-list'),
  upsertCatalogTerm: (input: {
    id?: string;
    kind: CatalogTermKind;
    en: string;
    hy: string;
    ru: string;
    aliases: Array<{ locale: 'en' | 'hy' | 'ru'; surface: string }>;
  }) => adminRequest<CatalogTerm>('catalog-term-upsert', input),
  deleteCatalogTerm: (id: string) =>
    adminRequest<{ id: string }>('catalog-term-delete', { id }),
  applyAction: (input: AdminActionInput) =>
    adminRequest<JsonRecord>('apply-action', {
      ...input,
      requestId: input.requestId ?? crypto.randomUUID(),
    }),
};
